import React, { Suspense, useEffect } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { IntlProvider } from 'react-intl'
import { Provider } from 'react-redux'
import { createBrowserRouter, RouterProvider } from 'react-router'
import { useAsync, useCookie } from 'react-use'
import { Alert, LinearProgress } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import flatten from 'flat'

import { YupErrorMapProvider } from '@lib/forms'
import { vantageTheme } from '@lib/theme'
import { config } from '@lib/ui-config'

import { loadLibraryMessages } from '../i18n/generated/libraryMessages'
import * as defaultMessages from '../i18n/locales/default.json'
import { Configuration, updateConfig } from '../redux/slices/configurationSlice'
import { setupStore } from '../redux/store'
import { routes } from '../routes'

type Messages = Record<string, string>

/**
 * Library messages merge under the app's own, so app keys win collisions. A failed library
 * catalog degrades to the app's own messages instead of failing the whole load.
 */
const loadMessages = async (): Promise<Messages> => {
  const appMessages: Messages = flatten(defaultMessages)

  try {
    const libraryMessages = await loadLibraryMessages()

    return { ...libraryMessages, ...appMessages }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load library messages:', error)

    return appMessages
  }
}

type AppLoadErrorProps = {
  error: Error | undefined
}

/**
 * The catalog is what failed, so nothing is left to translate with and the copy is hardcoded
 * English rather than an `IntlProvider` string.
 */
const AppLoadError = ({ error }: AppLoadErrorProps) => {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error('Failed to build the message catalog:', error)
  }, [error])

  return <Alert severity="error">We could not load this page. Refresh to try again.</Alert>
}

declare global {
  interface Window {
    config: any
  }
}

if (!window.config) {
  window.config = {
    ...config,
  }
}

export type AppProps = {
  publicPath: string | undefined
}

export function App({ publicPath }: AppProps) {
  const [csrfToken] = useCookie('csrftoken')
  const { value: messages, error, loading } = useAsync(loadMessages, [])

  if (loading) {
    return <LinearProgress />
  }

  if (error || !messages) {
    return <AppLoadError error={error} />
  }

  const configurationType: Configuration = {
    publicPath,
    csrfToken: csrfToken || '',
  }

  const store = setupStore()
  store.dispatch(updateConfig(configurationType))

  const router = createBrowserRouter(routes)

  return (
    <HelmetProvider>
      <IntlProvider locale="en" messages={messages}>
        <YupErrorMapProvider>
          <ThemeProvider theme={vantageTheme}>
            <Provider store={store}>
              <Suspense fallback={<LinearProgress />}>
                <RouterProvider router={router} />
              </Suspense>
            </Provider>
          </ThemeProvider>
        </YupErrorMapProvider>
      </IntlProvider>
    </HelmetProvider>
  )
}

export default App
